/**
 * RailCard — collapsible right-rail card from the prototype
 * (cms-redesign-mockup.html ~4958–4974, used by `.publish-card`,
 * `.settings-card`, and `.preview-card`).
 *
 *   <RailCard
 *     variant="publish"           // publish | settings | preview
 *     head={
 *       <RailCardHead title="Publishing" pill={<TierPill isFree />} />
 *     }
 *   >
 *     <div className="publish-body">…</div>
 *   </RailCard>
 *
 * Behavior:
 *   • Click the header → toggles collapsed (`open` boolean state)
 *   • Chevron rotates -90° on collapse (mirrors the prototype's
 *     `.rail-card.collapsed .rail-card-chev` rule)
 *   • Body wrapper is `display: none` when collapsed — keeps the DOM
 *     mounted so internal state survives toggling
 *
 * Variants share the same surface chrome (sand border, radius-lg,
 * overflow hidden); preview gets an extra drop-shadow per prototype.
 */
import React, { useState } from 'react';
import { cn } from '../cn';

const VARIANT_STYLES = {
  publish: {
    boxShadow: undefined,
  },
  settings: {
    boxShadow: undefined,
  },
  preview: {
    boxShadow: '0 4px 16px #2C3E5014',
  },
};

const Chevron = ({ open }) => (
  <span
    aria-hidden
    style={{
      width: 22,
      height: 22,
      display: 'inline-grid',
      placeItems: 'center',
      color: '#9CA1A8', /* text-muted */
      transition: 'transform 240ms cubic-bezier(0.22,1,0.36,1)',
      transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
      flexShrink: 0,
    }}
  >
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ width: 12, height: 12 }}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  </span>
);

/**
 * RailCardHead — shared header recipe.
 *
 * - publish/settings use the cream → surface gradient bg with a
 *   sand bottom border (`.publish-head` / `.settings-head` in the
 *   prototype, lines 3042–3047 and 4983–4988).
 * - preview uses a flat surface bg with the same sand border
 *   (`.preview-head`, lines 5023–5028).
 */
export function RailCardHead({ title, pill, variant = 'publish', onToggle, open, eyebrow }) {
  const isPreview = variant === 'preview';
  return (
    <div
      onClick={onToggle}
      className="rail-card-head flex items-center"
      style={{
        cursor: 'pointer',
        userSelect: 'none',
        padding: isPreview ? '10px 14px' : '14px 18px',
        background: isPreview
          ? 'rgb(var(--color-surface))' /* surface */
          : 'linear-gradient(180deg, rgb(var(--color-cream)) 0%, rgb(var(--color-surface)) 100%)',
        borderBottom: open ? '1px solid rgb(var(--color-sand))' /* sand */ : '1px solid transparent',
        gap: 8,
      }}
    >
      {eyebrow ? (
        <span
          className="font-condensed font-bold uppercase text-text-muted"
          style={{
            fontSize: 10.5,
            letterSpacing: '0.1em',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flex: 1,
            minWidth: 0,
          }}
        >
          <span
            aria-hidden
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: 'rgb(var(--color-teal-green))', /* teal-green */
              boxShadow: '0 0 0 3px #75A29333', /* teal-green-soft */
              flexShrink: 0,
            }}
          />
          {eyebrow}
        </span>
      ) : (
        <h3
          className="font-condensed font-bold text-navy"
          style={{ fontSize: 14, flex: 1, minWidth: 0 }}
        >
          {title}
        </h3>
      )}
      {pill}
      <Chevron open={open} />
    </div>
  );
}

export default function RailCard({
  variant = 'publish',
  defaultOpen = true,
  children,
  className,
  head, // function (open, toggle) => ReactNode OR ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen);
  const toggle = () => setOpen((v) => !v);

  const headEl = typeof head === 'function' ? head({ open, toggle }) : head;

  return (
    <div
      className={cn('bg-surface border border-sand', className)}
      style={{
        borderRadius: 12,
        overflow: 'hidden',
        ...VARIANT_STYLES[variant],
      }}
    >
      {headEl}
      {open && <>{children}</>}
    </div>
  );
}
